import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { CITY_OPTIONS, getDistrictOptions, resolveCanonicalCityName } from '../constants/cityDistrictOptions';
import { registerWebPushSubscription } from '../lib/push/webPushClient';

const REGISTRATION_DRAFT_KEY = 'bbtv_registration_survey_draft_v1';
const BRIDGE_EVENT_SURFACE = 'push_bridge';

const DEFAULT_MEAL_PREFERENCES = {
  breakfast: { enabled: true, time: '08:00', label: 'Kahvaltı' },
  lunch: { enabled: true, time: '13:00', label: 'Öğle Yemeği' },
  dinner: { enabled: true, time: '19:30', label: 'Akşam Yemeği' }
};

const QUESTIONS = [
  {
    id: 'q1',
    number: 1,
    label: 'Rutininiz?',
    type: 'radio',
    columns: 'grid-cols-2 md:grid-cols-4',
    options: [
      { label: 'Yoğun Çalışan', icon: 'work' },
      { label: 'Ev Hanımı/Erkeği', icon: 'home' },
      { label: 'Öğrenci', icon: 'school' },
      { label: 'Emekli', icon: 'chair' }
    ]
  },
  {
    id: 'q2',
    number: 2,
    label: 'Mutfak Beceri Seviyeniz?',
    type: 'radio',
    columns: 'grid-cols-1 md:grid-cols-3',
    options: [
      { label: 'Acemi Şef', icon: 'egg_alt' },
      { label: 'Orta Seviye', icon: 'tune' },
      { label: 'Mutfak Ustası', icon: 'verified' }
    ]
  },
  {
    id: 'q3',
    number: 3,
    label: 'Kaç Kişilik Yemek Pişiriyorsunuz?',
    type: 'radio',
    columns: 'grid-cols-5',
    compact: true,
    options: [
      { label: '1' },
      { label: '2' },
      { label: '3' },
      { label: '4' },
      { label: '5+' }
    ]
  },
  {
    id: 'q4',
    number: 4,
    label: 'Beslenme Tercihiniz?',
    type: 'radio',
    columns: 'grid-cols-2 md:grid-cols-4',
    options: [
      { label: 'Etçil', icon: 'kebab_dining' },
      { label: 'Vejetaryen', icon: 'spa' },
      { label: 'Pesketaryen', icon: 'set_meal' },
      { label: 'Vegan', icon: 'eco' }
    ]
  },
  {
    id: 'q5',
    number: 5,
    label: 'Gıda Hassasiyetiniz Var mı?',
    type: 'checkbox',
    columns: 'grid-cols-2 md:grid-cols-3',
    options: [
      { label: 'Yok' },
      { label: 'Laktoz' },
      { label: 'Gluten' },
      { label: 'Kuruyemiş' },
      { label: 'Yumurta' },
      { label: 'Balık' },
      { label: 'Soya' },
      { label: 'Susam' },
      { label: 'Deniz Ürünleri' }
    ]
  },
  {
    id: 'q7',
    number: 6,
    label: 'Mutfak Harcaması Takibi?',
    type: 'radio',
    columns: 'grid-cols-1 md:grid-cols-3',
    compact: true,
    options: [
      { label: 'Evet' },
      { label: 'Hayır ama istiyorum' },
      { label: 'Hayır, gerek yok' }
    ]
  },
  {
    id: 'q8',
    number: 7,
    label: 'Lütfen öğün ve bildirim (saat kaçta hatırlatalım) tercihlerinizi yapın?',
    type: 'meal_preferences'
  }
];

function lowerTr(v) {
  return String(v || '').toLocaleLowerCase('tr-TR');
}

function cloneMealPreferences(source = DEFAULT_MEAL_PREFERENCES) {
  return {
    breakfast: { ...DEFAULT_MEAL_PREFERENCES.breakfast, ...(source?.breakfast || {}) },
    lunch: { ...DEFAULT_MEAL_PREFERENCES.lunch, ...(source?.lunch || {}) },
    dinner: { ...DEFAULT_MEAL_PREFERENCES.dinner, ...(source?.dinner || {}) }
  };
}

function normalizeMealPreferences(raw) {
  if (!raw) return cloneMealPreferences();

  if (typeof raw === 'object' && !Array.isArray(raw) && (raw.breakfast || raw.lunch || raw.dinner)) {
    return cloneMealPreferences({
      breakfast: {
        enabled: raw?.breakfast?.enabled !== false,
        time: String(raw?.breakfast?.time || DEFAULT_MEAL_PREFERENCES.breakfast.time)
      },
      lunch: {
        enabled: raw?.lunch?.enabled !== false,
        time: String(raw?.lunch?.time || DEFAULT_MEAL_PREFERENCES.lunch.time)
      },
      dinner: {
        enabled: raw?.dinner?.enabled !== false,
        time: String(raw?.dinner?.time || DEFAULT_MEAL_PREFERENCES.dinner.time)
      }
    });
  }

  const values = Array.isArray(raw) ? raw.map(String) : [String(raw)];
  const hasBreakfast = values.some((v) => v.includes('Sabah') || v.includes('09:00') || v.includes('08:00'));
  const hasLunch = values.some((v) => v.includes('Öğle') || v.includes('Ogle') || v.includes('13:00'));
  const hasDinner = values.some((v) => v.includes('Akşam') || v.includes('Aksam') || v.includes('17:00') || v.includes('18:00') || v.includes('19:30'));

  return cloneMealPreferences({
    breakfast: { enabled: hasBreakfast },
    lunch: { enabled: hasLunch },
    dinner: { enabled: hasDinner }
  });
}

function mergeWithInitial(initial, incoming = {}) {
  const next = { ...initial };
  for (const key of Object.keys(next)) {
    if (key === 'q8') {
      next.q8 = normalizeMealPreferences(incoming.q8 || incoming.mealPreferences || incoming.notificationPreferences);
      continue;
    }
    if (Array.isArray(next[key])) {
      const value = incoming[key];
      next[key] = Array.isArray(value) ? value : (value ? [value] : []);
    } else if (incoming[key] !== undefined && incoming[key] !== null) {
      next[key] = incoming[key];
    }
  }
  if (incoming.city) next.city = incoming.city;
  if (incoming.district) next.district = incoming.district;
  return next;
}

function buildLegacyReminderArray(mealPreferences) {
  const values = [];
  if (mealPreferences?.breakfast?.enabled) values.push('Sabah');
  if (mealPreferences?.lunch?.enabled) values.push('Öğle');
  if (mealPreferences?.dinner?.enabled) values.push('Akşam');
  return values;
}

function buildReminderTimesArray(mealPreferences) {
  return ['breakfast', 'lunch', 'dinner']
    .filter((slot) => mealPreferences?.[slot]?.enabled)
    .map((slot) => String(mealPreferences?.[slot]?.time || '').trim())
    .filter(Boolean);
}

export default function RegistrationSurvey() {
  const navigate = useNavigate();
  const initial = useMemo(() => ({
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    q5: [],
    q7: '',
    q8: cloneMealPreferences(),
    city: '',
    district: ''
  }), []);

  const [answers, setAnswers] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [bridgeBusy, setBridgeBusy] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [districtQuery, setDistrictQuery] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const logOnboardingEvent = async (event, metadata = {}) => {
    try {
      await fetch(`${API_BASE_URL}/api/profile/onboarding/event`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          event,
          surface: BRIDGE_EVENT_SURFACE,
          mode: 'inline',
          reason: 'registration_push_bridge',
          metadata
        })
      });
    } catch (_err) {
      // no-op
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (res.status === 401) navigate('/giris-yap');
      } catch (_err) {
        // no-op
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REGISTRATION_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || typeof draft !== 'object') return;
      const merged = mergeWithInitial(initial, draft.answers || {});
      setAnswers(merged);
      setCityQuery(String(merged.city || ''));
      setDistrictQuery(String(merged.district || ''));
    } catch (_err) {
      // no-op
    }
  }, [initial]);

  useEffect(() => {
    const loadProfileDraft = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (!res.ok) return;
        const profile = await res.json();
        const intakeRaw = profile?.intakeSurvey;
        const intake = typeof intakeRaw === 'string' ? JSON.parse(intakeRaw) : intakeRaw;
        if (!intake || intake.completedRegistration === true) return;
        const merged = mergeWithInitial(initial, intake.answers || intake);
        if (intake.city && !merged.city) merged.city = intake.city;
        if (intake.district && !merged.district) merged.district = intake.district;
        setAnswers((prev) => mergeWithInitial(prev, merged));
        setCityQuery((prev) => prev || String(merged.city || ''));
        setDistrictQuery((prev) => prev || String(merged.district || ''));
      } catch (_err) {
        // no-op
      }
    };
    loadProfileDraft();
  }, [initial]);

  useEffect(() => {
    try {
      localStorage.setItem(REGISTRATION_DRAFT_KEY, JSON.stringify({
        savedAt: new Date().toISOString(),
        answers
      }));
    } catch (_err) {
      // no-op
    }
  }, [answers]);

  useEffect(() => {
    if (bridgeOpen) logOnboardingEvent('onboarding_push_bridge_rendered');
  }, [bridgeOpen]);

  const handleChange = (q, value, checked) => {
    if (q.type === 'checkbox') {
      setAnswers((prev) => {
        const current = Array.isArray(prev[q.id]) ? prev[q.id] : [];
        const isNoneOption = q.id === 'q5' && value === 'Yok';
        if (isNoneOption) {
          return { ...prev, [q.id]: checked ? ['Yok'] : [] };
        }
        const filtered = q.id === 'q5' ? current.filter((item) => item !== 'Yok') : current;
        const set = new Set(filtered);
        if (checked) set.add(value);
        else set.delete(value);
        return { ...prev, [q.id]: Array.from(set) };
      });
      return;
    }
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
  };

  const handleLocationChange = (field, value) => {
    setAnswers((prev) => {
      if (field === 'city') return { ...prev, city: value, district: '' };
      return { ...prev, [field]: value };
    });
    if (field === 'city') setDistrictQuery('');
  };

  const updateMealPreference = (slot, patch) => {
    setAnswers((prev) => ({
      ...prev,
      q8: {
        ...prev.q8,
        [slot]: {
          ...prev.q8[slot],
          ...patch
        }
      }
    }));
  };

  const filteredCities = useMemo(() => {
    const q = lowerTr(cityQuery);
    if (!q) return CITY_OPTIONS;
    return CITY_OPTIONS.filter((city) => lowerTr(city).includes(q));
  }, [cityQuery]);

  const districtOptions = useMemo(() => getDistrictOptions(answers.city), [answers.city]);
  const filteredDistricts = useMemo(() => {
    const q = lowerTr(districtQuery);
    if (!q) return districtOptions;
    return districtOptions.filter((district) => lowerTr(district).includes(q));
  }, [districtOptions, districtQuery]);

  const getMissingRequiredLabels = () => {
    const missing = [];
    for (const q of QUESTIONS) {
      if (q.type === 'meal_preferences') continue;
      const value = answers[q.id];
      if (q.type === 'checkbox') {
        if (!Array.isArray(value) || value.length === 0) missing.push(q.label);
        continue;
      }
      const text = String(value || '').trim();
      if (!text) {
        missing.push(q.label);
        continue;
      }
      if (q.id === 'q3' && text.startsWith('5')) {
        const parsed = Number.parseInt(text, 10);
        if (!Number.isFinite(parsed) || parsed < 5) {
          missing.push(`${q.label} (5+ için sayı gerekli)`);
        }
      }
    }

    if (String(answers.q7 || '').trim() === 'Evet') {
      if (!String(answers.city || '').trim()) missing.push('İl');
      if (!String(answers.district || '').trim()) missing.push('İlçe');
    }
    return missing;
  };

  const missingRequiredLabels = useMemo(getMissingRequiredLabels, [answers]);

  const proceedToStockOnboarding = () => {
    window.dispatchEvent(new Event('profileUpdated'));
    window.location.href = '/onboarding-setup';
  };

  const askPushPermission = async () => {
    setBridgeBusy(true);
    await logOnboardingEvent('onboarding_push_bridge_cta_clicked');

    try {
      if (!('Notification' in window)) {
        await logOnboardingEvent('onboarding_push_bridge_permission_result', { result: 'unsupported' });
        proceedToStockOnboarding();
        return;
      }

      if (Notification.permission === 'granted') {
        const subscribed = await registerWebPushSubscription({ requestPermission: false });
        await logOnboardingEvent('onboarding_push_bridge_permission_result', { result: 'granted_already' });
        await logOnboardingEvent('onboarding_push_bridge_subscription_result', {
          result: subscribed?.ok ? 'subscribed' : 'subscribe_failed',
          reason: subscribed?.reason || null
        });
        proceedToStockOnboarding();
        return;
      }

      if (Notification.permission === 'denied') {
        await logOnboardingEvent('onboarding_push_bridge_permission_result', { result: 'denied_already' });
        proceedToStockOnboarding();
        return;
      }

      const permission = await Notification.requestPermission();
      await logOnboardingEvent('onboarding_push_bridge_permission_result', { result: permission });
      if (permission === 'granted') {
        const subscribed = await registerWebPushSubscription({ requestPermission: false });
        await logOnboardingEvent('onboarding_push_bridge_subscription_result', {
          result: subscribed?.ok ? 'subscribed' : 'subscribe_failed',
          reason: subscribed?.reason || null
        });
      }
      proceedToStockOnboarding();
    } catch (_err) {
      await logOnboardingEvent('onboarding_push_bridge_permission_result', { result: 'error' });
      proceedToStockOnboarding();
    } finally {
      setBridgeBusy(false);
    }
  };

  const skipPushBridge = async () => {
    setBridgeBusy(true);
    await logOnboardingEvent('onboarding_push_bridge_skipped');
    setBridgeOpen(false);
    proceedToStockOnboarding();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = getMissingRequiredLabels();
    if (missing.length > 0) {
      setStatus({
        ok: false,
        message: `Lütfen tüm zorunlu soruları tamamlayın: ${missing.join(', ')}`
      });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    const normalizedAnswers = {
      q1: answers.q1,
      q2: answers.q2,
      q3: answers.q3,
      q4: answers.q4,
      q5: Array.isArray(answers.q5) ? answers.q5 : [],
      q7: answers.q7,
      q8: cloneMealPreferences(answers.q8)
    };

    const reminderTimes = buildReminderTimesArray(answers.q8);
    const legacyReminderChoices = buildLegacyReminderArray(answers.q8);

    normalizedAnswers.reminderTimes = reminderTimes;
    normalizedAnswers.sendTime = reminderTimes[0] || '';
    normalizedAnswers.notificationPreferences = cloneMealPreferences(answers.q8);
    normalizedAnswers.legacyReminderChoices = legacyReminderChoices;

    const intakeSurvey = {
      answers: normalizedAnswers,
      mealPreferences: cloneMealPreferences(answers.q8),
      notificationPreferences: cloneMealPreferences(answers.q8),
      reminderTimes,
      sendTime: reminderTimes[0] || '',
      completedRegistration: true,
      completedAt: new Date().toISOString()
    };

    const shouldTrackKitchenSpend = String(answers.q7 || '').trim() === 'Evet';
    const selectedCity = String(answers.city || '').trim();
    const selectedDistrict = String(answers.district || '').trim();

    if (shouldTrackKitchenSpend && (!selectedCity || !selectedDistrict)) {
      setStatus({ ok: false, message: 'Lütfen il ve ilçe seçimini tamamlayın.' });
      setSubmitting(false);
      return;
    }

    if (answers.q3) {
      const sVal = parseInt(answers.q3, 10);
      intakeSurvey.answers.serving = sVal || answers.q3;
      intakeSurvey.servingSize = sVal || Number(answers.q3) || 0;
    }

    if (answers.q5) {
      intakeSurvey.answers.preferences = Array.isArray(answers.q5) ? answers.q5 : [answers.q5];
      intakeSurvey.answers.pref = intakeSurvey.answers.preferences;
      intakeSurvey.preferences = intakeSurvey.answers.preferences;
    }

    if (answers.q4) {
      intakeSurvey.answers.diet = answers.q4;
      intakeSurvey.diet = answers.q4;
    }

    if (answers.q1) intakeSurvey.jobTitle = answers.q1;
    if (answers.q7) intakeSurvey.tracking = answers.q7;
    if (selectedCity) intakeSurvey.city = selectedCity;
    if (selectedDistrict) intakeSurvey.district = selectedDistrict;
    if (selectedCity || selectedDistrict) {
      intakeSurvey.specialDaysContact = { city: selectedCity, district: selectedDistrict };
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ intakeSurvey })
      });

      if (res.status === 401) {
        setStatus({ ok: false, message: 'Lütfen giriş yapın.' });
        navigate('/giris-yap');
        return;
      }
      if (!res.ok) {
        const errorPayload = await res.json().catch(() => ({}));
        const errorMessage = errorPayload?.message || errorPayload?.error || `Sunucu hatası (${res.status})`;
        throw new Error(errorMessage);
      }

      setStatus({ ok: true, message: 'Profil kaydedildi.' });
      localStorage.removeItem(REGISTRATION_DRAFT_KEY);
      setBridgeOpen(true);
    } catch (error) {
      const detail = String(error?.message || '').trim();
      setStatus({
        ok: false,
        message: detail && detail !== 'network_error'
          ? `Kaydedilemedi: ${detail}`
          : 'Kaydedilemedi, lütfen tekrar deneyin.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStandardOptions = (q) => (
    <div className={`grid ${q.columns} gap-4`}>
      {q.options.map((opt) => {
        const checked = q.type === 'checkbox'
          ? Array.isArray(answers[q.id]) && answers[q.id].includes(opt.label)
          : (String(answers[q.id]).startsWith('5') && opt.label === '5+')
            || answers[q.id] === opt.label;

        const cardBase = q.compact
          ? 'min-h-[54px] justify-center'
          : 'min-h-[128px] justify-center';

        return (
          <div key={opt.label} className="flex flex-col gap-2">
            <label
              className={[
                'cursor-pointer rounded-xl bg-white transition-all duration-200',
                'border px-4 text-center',
                checked ? 'border-[#F0847D] bg-[#fffaf9] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]' : 'border-[#e5e7eb] hover:border-[#F0847D] hover:bg-[#fffaf9]',
                q.compact ? 'py-4' : 'p-6',
                cardBase,
                'flex flex-col items-center gap-3'
              ].join(' ')}
            >
              <input
                className="hidden"
                name={q.id}
                type={q.type}
                checked={checked}
                onChange={(e) => handleChange(q, opt.label, e.target.checked)}
              />
              {q.type === 'radio' && opt.icon ? (
                <span className="material-symbols-outlined text-[24px] text-[#9ca3af]">
                  {opt.icon}
                </span>
              ) : null}
              <span className="text-sm font-medium text-[#374151]">
                {opt.label}
              </span>
              {q.type === 'checkbox' ? (
                <span className="mt-1">
                  <input
                    className="rounded border-gray-300 text-[#F0847D] focus:ring-[#F0847D]"
                    name={`${q.id}_${opt.label}`}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => handleChange(q, opt.label, e.target.checked)}
                  />
                </span>
              ) : null}
            </label>

            {q.id === 'q3' && opt.label === '5+' && checked ? (
              <input
                type="number"
                min="5"
                placeholder="Sayı girin"
                className="w-full rounded-lg border border-[#F0847D] bg-white p-2 text-center text-sm text-[#111827] outline-none focus:ring-1 focus:ring-[#F0847D]"
                value={parseInt(answers.q3, 10) >= 5 ? answers.q3 : ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, q3: e.target.value }))}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const renderMealPreferences = () => (
    <section className="space-y-6">
      <div className="flex items-center">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold mr-3">7</span>
        <h2 className="text-lg font-bold text-gray-900">Lütfen öğün ve bildirim (saat kaçta hatırlatalım) tercihlerinizi yapın?</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {['breakfast', 'lunch', 'dinner'].map((slot) => {
          const item = answers.q8[slot];
          return (
            <div
              key={slot}
              className="rounded-xl border border-[#e5e7eb] bg-white p-6 flex flex-col items-center justify-center space-y-4"
            >
              <span className="text-sm font-bold text-gray-900">{item.label}</span>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  className="rounded border-gray-300 text-[#F0847D] focus:ring-[#F0847D]"
                  type="checkbox"
                  checked={!item.enabled}
                  onChange={(e) => updateMealPreference(slot, { enabled: !e.target.checked })}
                />
                <span className="text-xs font-medium text-gray-500">Yapmıyorum</span>
              </label>
              <div className={`w-full flex items-center justify-center space-x-2 rounded-lg px-3 py-2 border ${item.enabled ? 'bg-gray-50 border-gray-100' : 'bg-gray-50/70 border-gray-100 opacity-55'}`}>
                <span className="material-symbols-outlined text-[16px] text-gray-400">schedule</span>
                <input
                  className="bg-transparent border-none p-0 text-sm font-bold text-gray-900 focus:ring-0 w-20 text-center"
                  type="time"
                  value={item.time}
                  disabled={!item.enabled}
                  onChange={(e) => updateMealPreference(slot, { time: e.target.value })}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 text-left">Bu saatleri daha sonra profilinden değiştirebilirsin.</p>
    </section>
  );

  return (
    <div className="min-h-screen bg-[#fcfcfc] px-4 py-4 md:px-8 md:py-8 lg:px-12 lg:py-12 text-[#111827]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Serif:wght@700&display=swap');
        .registration-survey-root {
          font-family: 'Inter', sans-serif;
        }
      `}</style>

      <main className="registration-survey-root max-w-4xl mx-auto space-y-12 pb-24">
        <header className="space-y-4">
          <div className="flex justify-between items-center text-[10px] font-bold tracking-widest text-slate-400 uppercase">
            <span>Anket İlerlemesi</span>
            <span className="bg-red-50 text-[#F0847D] px-3 py-1 rounded-full text-xs">7 Soru</span>
          </div>
          <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden">
            <div className="h-1 bg-[#ef4444] rounded-full w-full" />
          </div>
        </header>

        <form className="space-y-12" onSubmit={handleSubmit}>
          {QUESTIONS.map((q) => {
            if (q.type === 'meal_preferences') return <div key={q.id}>{renderMealPreferences()}</div>;

            return (
              <section key={q.id} className="space-y-6">
                <div className="flex items-center">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-sm font-semibold mr-3">
                    {q.number}
                  </span>
                  <h2 className="text-lg font-bold text-gray-900">{q.label}</h2>
                </div>

                {renderStandardOptions(q)}

                {q.id === 'q7' && String(answers.q7 || '').trim() === 'Evet' ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold text-[#637588] uppercase tracking-wider">
                        Fiyatlar bulunduğun bölgeye göre değişebilir - İl
                      </label>
                      <input
                        type="text"
                        value={cityQuery}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCityQuery(value);
                          handleLocationChange('city', resolveCanonicalCityName(value));
                        }}
                        list="registration-survey-city-options"
                        placeholder="İl yazın veya seçin"
                        className="w-full rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-sm"
                      />
                      <datalist id="registration-survey-city-options">
                        {filteredCities.map((city) => <option key={city} value={city} />)}
                      </datalist>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold text-[#637588] uppercase tracking-wider">
                        İlçe
                      </label>
                      <input
                        type="text"
                        value={districtQuery}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDistrictQuery(value);
                          const exact = districtOptions.find((district) => lowerTr(district) === lowerTr(value));
                          handleLocationChange('district', exact || '');
                        }}
                        list="registration-survey-district-options"
                        placeholder={answers.city ? 'İlçe yazın veya seçin' : 'Önce il seçin'}
                        disabled={!answers.city}
                        className="w-full rounded-lg border border-[#e5e7eb] bg-white p-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      />
                      <datalist id="registration-survey-district-options">
                        {filteredDistricts.map((district) => <option key={district} value={district} />)}
                      </datalist>
                    </div>
                  </div>
                ) : null}
              </section>
            );
          })}

          <hr className="border-gray-100" />

          <footer className="flex flex-col space-y-6">
            <div className="flex justify-between items-center">
              <button
                className="text-sm font-medium text-gray-500 flex items-center hover:text-gray-900 transition-colors"
                type="button"
                onClick={() => navigate(-1)}
              >
                <span className="material-symbols-outlined text-[18px] mr-2">arrow_back</span>
                Geri
              </button>

              <button
                className="bg-[#F0847D] text-white px-8 py-3 rounded-lg font-bold text-sm shadow-lg shadow-[#F0847D]/20 hover:bg-[#ee746c] transition-all flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
                type="submit"
                disabled={submitting || missingRequiredLabels.length > 0}
              >
                {submitting ? 'Kaydediliyor...' : 'Anketi Bitir'}
                <span className="material-symbols-outlined text-[18px] ml-2">check</span>
              </button>
            </div>

            {missingRequiredLabels.length > 0 ? (
              <p className="text-center text-xs font-bold text-orange-500">Devam etmek için tüm soruları yanıtlayın.</p>
            ) : null}

            {status ? (
              <p className={`text-sm font-semibold text-center ${status.ok ? 'text-green-600' : 'text-red-600'}`}>
                {status.message}
              </p>
            ) : null}
          </footer>
        </form>

        {bridgeOpen ? (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl border border-[#e6dbdb]">
              <h4 className="text-lg font-bold text-[#181111]">Harika, saat tercihlerini kaydettik.</h4>
              <p className="mt-2 text-sm text-[#637588]">
                Seçtiğin saatlerde yemek hatırlatması gönderebilmemiz için bildirim izni gerekiyor.
              </p>

              <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={skipPushBridge}
                  disabled={bridgeBusy}
                  className="rounded-lg h-11 px-4 font-semibold border border-[#d8caca] text-[#5d4b4b] disabled:opacity-60"
                >
                  Şimdilik Geç
                </button>
                <button
                  type="button"
                  onClick={askPushPermission}
                  disabled={bridgeBusy}
                  className="rounded-lg h-11 px-5 bg-[#F0847D] text-white font-bold disabled:opacity-60"
                >
                  {bridgeBusy ? 'İşleniyor...' : 'Bildirimlere İzin Ver'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
